import {
    createContext,
    FC,
    PropsWithChildren,
    useContext,
    useState,
    useEffect,
} from "react";
import { ConfigProvider as AntdConfigProvider, theme } from "antd";

type Mode = "light" | "dark";

type ConfigProviderContext = {
    mode: Mode;
    setMode: (mode: Mode) => void;
    toggleMode: () => void;
};

export const ConfigProviderContext = createContext<
    ConfigProviderContext | undefined
>(undefined);

const THEME_STORAGE_KEY = "splitfacil-theme";

// Função para obter o tema inicial
const getInitialTheme = (): Mode => {
    // Verifica se há preferência salva
    const savedTheme = localStorage.getItem(THEME_STORAGE_KEY) as Mode;
    if (savedTheme === "light" || savedTheme === "dark") {
        return savedTheme;
    }
    
    // Verifica a preferência do sistema
    if (window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches) {
        return "dark";
    }
    
    return "light";
};

export const ConfigProvider: FC<PropsWithChildren> = ({ children }) => {
    const [mode, setMode] = useState<Mode>(getInitialTheme);

    // Persistir tema no localStorage e aplicar no documento
    useEffect(() => {
        localStorage.setItem(THEME_STORAGE_KEY, mode);
        document.documentElement.setAttribute("data-theme", mode);
        document.body.setAttribute("data-theme", mode);
    }, [mode]);

    // Função para alternar entre temas
    const toggleMode = () => {
        setMode((prevMode) => (prevMode === "light" ? "dark" : "light"));
    };

    // Função para definir o tema manualmente
    const handleSetMode = (newMode: Mode) => {
        setMode(newMode);
    };

    return (
        <ConfigProviderContext.Provider value={{ mode, setMode: handleSetMode, toggleMode }}>
            <AntdConfigProvider
                theme={{
                    algorithm:
                        mode === "light"
                            ? theme.defaultAlgorithm
                            : theme.darkAlgorithm,
                    token: {
                        colorPrimary: mode === "light" ? "#2563eb" : "#3b82f6",
                        colorSuccess: "#10b981",
                        colorWarning: "#f59e0b",
                        colorError: "#ef4444",
                        colorInfo: "#3b82f6",
                        borderRadius: 8,
                        wireframe: false,
                    },
                    components: {
                        Card: {
                            borderRadius: 12,
                        },
                        Button: {
                            borderRadius: 8,
                        },
                        Input: {
                            borderRadius: 8,
                        },
                        Menu: {
                            borderRadius: 8,
                        },
                    },
                }}
            >
                {children}
            </AntdConfigProvider>
        </ConfigProviderContext.Provider>
    );
};

export const useConfigProvider = () => {
    const context = useContext(ConfigProviderContext);

    if (context === undefined) {
        throw new Error(
            "useConfigProvider must be used within a ConfigProvider",
        );
    }

    return context;
};
